import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import api, { setAccessToken, clearAccessToken, whenAccessTokenReady, clearInFlightCache } from "@/lib/api";

const AuthContext = createContext(null);

// V2.2E-R · Permission-state machine
// -----------------------------------
// Previous behavior collapsed every permissions-fetch failure into
// `setPermissions({})`, which is INDISTINGUISHABLE from a real user
// with zero capabilities. That prevented the SPA from ever retrying
// a genuinely transient permissions fetch and made
// `can("sessions.write")` unconditionally false after any 401 —
// even after the very next successful refresh + fetch would have
// resolved the capability. This is the "chip stays readonly forever"
// bug documented in `V2_2E_CLOSURE_REPORT.md § Step 5C`.
//
// New behavior tracks four explicit states on `permissionsState`:
//   * "unknown"       — never attempted (fresh mount, no login yet)
//   * "loading"       — request in flight
//   * "loaded"        — capabilities object returned by server
//   * "failed"        — request errored (network, 401, 5xx)
// `can(capability)` returns false unless `permissionsState === "loaded"`.
// A generation counter (`authGenerationRef`) prevents an older
// request's response from clobbering a newer login's result. The
// counter is bumped by `loginWithToken` and `logout` — the only two
// moments the auth identity changes.
//
// Nothing in this fix changes authority logic. Backend cutover /
// canary flags are untouched.
const CHECKAUTH_TIMEOUT_MS = 6000;
const _SS_USER_KEY = "pec_user_v1";
const PERM_STATE_UNKNOWN = "unknown";
const PERM_STATE_LOADING = "loading";
const PERM_STATE_LOADED  = "loaded";
const PERM_STATE_FAILED  = "failed";

function _hydrateUser() {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    const raw = window.sessionStorage.getItem(_SS_USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Sanity: must at least have a user_id or id field. Anything else
    // is a corrupted / stale write from an older schema — discard it.
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.user_id && !parsed.id) return null;
    return parsed;
  } catch {
    return null;
  }
}

function _persistUser(u) {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return;
    if (u) window.sessionStorage.setItem(_SS_USER_KEY, JSON.stringify(u));
    else window.sessionStorage.removeItem(_SS_USER_KEY);
  } catch { /* no-op */ }
}


export const AuthProvider = ({ children }) => {
  // Fix-attempt-2: hydrate user + loading from sessionStorage
  // synchronously so a full-page deep-link boot after login renders
  // with the guard already satisfied. `loading` starts false when we
  // have a durable snapshot — checkAuth still runs in the background
  // as a freshness probe and will overwrite/clear on real changes.
  const _hydrated = _hydrateUser();
  const [user, setUser] = useState(_hydrated);
  const [loading, setLoading] = useState(!_hydrated);
  // V2.2E-R · Explicit 4-state permission machine (see file-top
  // comment). Never collapse a failed fetch into `{}` — that made
  // failed indistinguishable from "loaded, no capabilities".
  const [permissions, setPermissions] = useState(null);
  const [permissionsState, setPermissionsState] = useState(PERM_STATE_UNKNOWN);
  const authGenerationRef = useRef(0);
  const permGenerationRef = useRef(0);
  const queryClient = useQueryClient();

  // V2.2E-R2 · Bounded retry state.
  // ────────────────────────────────────────────────────────────────
  // Prior behavior (V2.2E-R): a `useEffect` scheduled a 500 ms retry
  // whenever `permissionsState === "failed"`. That created an
  // infinite loop for persistent failures (401/403/500): failed →
  // timer → fetchPermissions → loading → failed → new timer …
  //
  // New behavior:
  //   * `permRetryUsedRef` tracks whether we've already spent the
  //     one automatic retry allowance for the CURRENT auth
  //     generation.
  //   * `permRetryTimerRef` holds the pending timer id so login /
  //     logout / manual refresh can cancel it deterministically.
  //   * The allowance is RESET only by (a) a new `loginWithToken`
  //     (fresh generation) or (b) a successful load (state='loaded').
  //   * After the one bounded retry fails, we stay in 'failed' with
  //     NO further network traffic. Manual `refreshPermissions()`
  //     still works — it explicitly re-arms and dispatches.
  const permRetryUsedRef = useRef(false);
  const permRetryTimerRef = useRef(null);

  function _clearRetryTimer() {
    if (permRetryTimerRef.current !== null) {
      clearTimeout(permRetryTimerRef.current);
      permRetryTimerRef.current = null;
    }
  }

  const fetchPermissions = useCallback(async () => {
    _clearRetryTimer();
    const gen = ++permGenerationRef.current;
    const authGen = authGenerationRef.current;
    setPermissionsState(PERM_STATE_LOADING);
    try {
      await whenAccessTokenReady();
      const res = await api.get("/whoami/permissions");
      // Drop stale response if a newer permissions request has
      // started OR the auth identity changed under us.
      if (permGenerationRef.current !== gen) return;
      if (authGenerationRef.current !== authGen) return;
      setPermissions(res.data?.capabilities || {});
      setPermissionsState(PERM_STATE_LOADED);
      // Successful load resets the retry allowance so a later
      // transient failure gets another single automatic attempt.
      permRetryUsedRef.current = false;
    } catch (_err) {
      if (permGenerationRef.current !== gen) return;
      if (authGenerationRef.current !== authGen) return;
      // Do NOT overwrite the last-known-good `permissions` — leave it
      // as-is so a brief blip does not flip an editable chip readonly.
      setPermissionsState(PERM_STATE_FAILED);
      // Schedule EXACTLY ONE automatic retry per auth generation.
      // If the allowance is already spent, stay in 'failed' with no
      // further requests — manual retry via refreshPermissions()
      // remains available to the UI.
      if (!permRetryUsedRef.current) {
        permRetryUsedRef.current = true;
        const retryForAuthGen = authGenerationRef.current;
        permRetryTimerRef.current = setTimeout(() => {
          permRetryTimerRef.current = null;
          // Guard: the auth generation may have changed while the
          // timer waited (login/logout). Only fire if we're still
          // on the same generation the retry was scheduled for.
          if (authGenerationRef.current !== retryForAuthGen) return;
          fetchPermissions();
        }, 500);
      }
    }
  }, []);

  const checkAuth = useCallback(async () => {
    const gen = authGenerationRef.current;
    // Ceiling timer — if we don't hear back within CHECKAUTH_TIMEOUT_MS
    // we flip loading=false so Protected can render. Without this,
    // an unresolved axios promise (network stall on /auth/refresh, or
    // a stuck in-flight dedupe entry) leaves the app on
    // "Loading operations…" indefinitely — the reported blocker.
    //
    // Fix-attempt-2: the ceiling now flips loading=false WITHOUT
    // touching `user`. If we hydrated a durable snapshot at mount,
    // `user` remains truthy and the guard renders the target page
    // (real /auth/me may still resolve later and overwrite). If we
    // did NOT hydrate, `user` is null and Protected bounces to
    // /login — the correct behavior when the backend is unreachable.
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      if (authGenerationRef.current === gen) {
        timedOut = true;
        setLoading(false);
      }
    }, CHECKAUTH_TIMEOUT_MS);
    try {
      const res = await api.get("/auth/me");
      if (authGenerationRef.current !== gen) return;
      setUser(res.data);
      _persistUser(res.data);
      fetchPermissions();
    } catch {
      if (authGenerationRef.current !== gen) return;
      // Genuine session death (backend explicitly rejected auth +
      // refresh). Wipe every trace so the next boot doesn't render
      // a stale snapshot.
      setUser(null);
      setPermissions(null);
      setPermissionsState(PERM_STATE_UNKNOWN);
      clearAccessToken();
      _persistUser(null);
    } finally {
      clearTimeout(timeoutId);
      if (!timedOut && authGenerationRef.current === gen) setLoading(false);
    }
  }, [fetchPermissions]);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginWithToken = useCallback((token, userData) => {
    authGenerationRef.current += 1;
    // V2.2E-R2 · Fresh auth generation resets the bounded-retry
    // allowance and cancels any pending automatic retry from the
    // previous generation.
    _clearRetryTimer();
    permRetryUsedRef.current = false;
    setAccessToken(token);
    sessionStorage.setItem("pec_welcome", userData?.name || userData?.email || "back");
    setUser(userData);
    _persistUser(userData);
    setLoading(false);
    // Poisoned-401 recovery: any TanStack Query result that ran during
    // the cold-start probes is now stale/error-cached. Blow away the
    // whole cache so first-navigation queries after login refetch with
    // the fresh access token. Without this step the reported hang
    // reproduced deterministically — post-login navigation to Schedule
    // saw error-cached queries and never re-fired.
    //
    // Session 58 (Feb 2026) — ALSO clear the axios `_inFlight` dedupe
    // map. TanStack sits ABOVE axios; a poisoned axios-level dedupe
    // entry (stuck promise from a pre-login silent-refresh stall) can
    // hang the very first post-login GET even if TanStack itself is
    // pristine. Login and logout are the only two moments the auth
    // identity changes, so a full clear is semantically correct.
    try {
      queryClient.getQueryCache().clear();
      queryClient.getMutationCache().clear();
    } catch {
      /* no-op if provider not mounted (e.g. auth tests) */
    }
    clearInFlightCache();
    fetchPermissions();
  }, [fetchPermissions, queryClient]);

  const logout = useCallback(async () => {
    authGenerationRef.current += 1;
    // V2.2E-R2 · Cancel any pending bounded retry from the previous
    // generation and clear the allowance so a subsequent login
    // starts from a known state.
    _clearRetryTimer();
    permRetryUsedRef.current = false;
    try { await api.post("/auth/logout"); } catch (e) { console.warn("Logout request failed; clearing local session anyway", e); }
    clearAccessToken();
    sessionStorage.removeItem("pec_welcome");
    _persistUser(null);
    setUser(null);
    setPermissions(null);
    setPermissionsState(PERM_STATE_UNKNOWN);
    try {
      queryClient.getQueryCache().clear();
      queryClient.getMutationCache().clear();
    } catch { /* no-op */ }
    clearInFlightCache();
  }, [queryClient]);

  // Phase 17f Task C1 — `can(capability)` helper.
  // V2.2E-R · Fail-closed while loading/failed/unknown; only affirm
  // capabilities when the state machine reports `loaded`.
  const can = useCallback((capability) => {
    if (!user) return false;
    if (permissionsState !== PERM_STATE_LOADED) return false;
    if (!permissions) return false;
    return permissions[capability] === true;
  }, [user, permissions, permissionsState]);

  const value = useMemo(
    // Second-pass code-review Item G (Feb 2026): 7 deps flagged as above
    // threshold, but auth-context state IS naturally clustered (user +
    // permissions + 4 stable useCallbacks + loading flag). Splitting the
    // memo would fragment a single consumer contract for zero perf gain —
    // the 4 callbacks are already useCallback-stabilized, so the memo only
    // recomputes on real state transitions (login/logout/perm-refresh).
    () => ({
      user, setUser, loading, checkAuth, loginWithToken, logout,
      canEdit: !!user?.can_edit,
      // C1 additions — granular capability gate.
      permissions, can,
      // V2.2E-R additions — expose the state machine + a manual
      // refresh trigger so callers (e.g. auth-race regression test,
      // future UI badges) can observe hydration progress.
      permissionsState, refreshPermissions: fetchPermissions,
    }),
    [user, loading, checkAuth, loginWithToken, logout, permissions, can, permissionsState, fetchPermissions]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
